import FW from '../fw/fw';
import Logger from '../fw/logger';
import Language from '../fw/defaults/language';
import AdsHelpers from './ads-helpers';


export default class AdsImaDai {

  #rmp;
  #imaDaiLibLoadedInterval = null;
  #imaDaiStreamManager = null;
  #imaDaiAdCurrentTime = -1;
  #imaDaiIsSkippable = false;
  #imaDaiErrorMessage = '';
  #imaDaiClickElement = null;

  constructor(rmp) {
    this.#rmp = rmp;
  }

  get adCurrentTime() {
    return this.#imaDaiAdCurrentTime;
  }

  get isSkippable() {
    return this.#imaDaiIsSkippable;
  }

  get errorMessage() {
    return this.#imaDaiErrorMessage;
  }

  #imaDaiRequestVODStream(
    cmsId,
    videoId,
    apiKey,
    adTagParameters,
    streamActivityMonitorId,
    authToken,
    omidAccessModeRules
  ) {
    const streamRequest = new google.ima.dai.api.VODStreamRequest();
    streamRequest.format = 'hls';
    streamRequest.contentSourceId = cmsId;
    streamRequest.videoId = videoId;
    if (apiKey) {
      streamRequest.apiKey = apiKey;
    }
    if (!FW.isEmptyObject(adTagParameters)) {
      streamRequest.adTagParameters = adTagParameters;
    }
    if (streamActivityMonitorId) {
      streamRequest.streamActivityMonitorId = streamActivityMonitorId;
    }
    if (authToken) {
      streamRequest.authToken = authToken;
    }
    if (omidAccessModeRules) {
      streamRequest.omidAccessModeRules = omidAccessModeRules;
    }
    Logger.print('google-ima-dai', 'requestVODStream');
    this.#imaDaiStreamManager.requestStream(streamRequest);
  }

  #imaDaiRequestLiveStream(
    assetKey,
    apiKey,
    adTagParameters,
    streamActivityMonitorId,
    authToken,
    omidAccessModeRules
  ) {
    const streamRequest = new google.ima.dai.api.LiveStreamRequest();
    streamRequest.format = 'hls';
    streamRequest.assetKey = assetKey;
    if (apiKey) {
      streamRequest.apiKey = apiKey;
    }
    if (!FW.isEmptyObject(adTagParameters)) {
      streamRequest.adTagParameters = adTagParameters;
    }
    if (streamActivityMonitorId) {
      streamRequest.streamActivityMonitorId = streamActivityMonitorId;
    }
    if (authToken) {
      streamRequest.authToken = authToken;
    }
    if (omidAccessModeRules) {
      streamRequest.omidAccessModeRules = omidAccessModeRules;
    }
    Logger.print('google-ima-dai', 'requestLiveStream');
    this.#imaDaiStreamManager.requestStream(streamRequest);
  }

  #imaDaiRequestPodStream(
    customAssetKey,
    networkCode,
    apiKey,
    adTagParameters,
    streamActivityMonitorId,
    authToken,
    omidAccessModeRules
  ) {
    const streamRequest = new google.ima.dai.api.PodStreamRequest();
    streamRequest.format = 'hls';
    if (customAssetKey) {
      streamRequest.customAssetKey = customAssetKey;
    }
    if (networkCode) {
      streamRequest.networkCode = networkCode;
    }
    if (apiKey) {
      streamRequest.apiKey = apiKey;
    }
    if (!FW.isEmptyObject(adTagParameters)) {
      streamRequest.adTagParameters = adTagParameters;
    }
    if (streamActivityMonitorId) {
      streamRequest.streamActivityMonitorId = streamActivityMonitorId;
    }
    if (authToken) {
      streamRequest.authToken = authToken;
    }
    if (omidAccessModeRules) {
      streamRequest.omidAccessModeRules = omidAccessModeRules;
    }
    Logger.print('google-ima-dai', 'requestPodStream');
    this.#imaDaiStreamManager.requestStream(streamRequest);
  }

  #imaDaiRequestPodVodStreamRequest(
    networkCode,
    apiKey,
    adTagParameters,
    streamActivityMonitorId,
    authToken,
    omidAccessModeRules
  ) {
    const streamRequest = new google.ima.dai.api.PodVodStreamRequest();
    streamRequest.format = 'hls';
    if (networkCode) {
      streamRequest.networkCode = networkCode;
    }
    if (apiKey) {
      streamRequest.apiKey = apiKey;
    }
    if (!FW.isEmptyObject(adTagParameters)) {
      streamRequest.adTagParameters = adTagParameters;
    }
    if (streamActivityMonitorId) {
      streamRequest.streamActivityMonitorId = streamActivityMonitorId;
    }
    if (authToken) {
      streamRequest.authToken = authToken;
    }
    if (omidAccessModeRules) {
      streamRequest.omidAccessModeRules = omidAccessModeRules;
    }
    Logger.print('google-ima-dai', 'requestPodStream');
    this.#imaDaiStreamManager.requestStream(streamRequest);
  }

  #imaDaiOnAdProgress(e) {
    if (e && typeof e.getStreamData === 'function') {
      const adProgressData = e.getStreamData().adProgressData;
      const adPosition = adProgressData.adPosition;
      const totalAds = adProgressData.totalAds;
      const currentTime = adProgressData.currentTime;
      this.#imaDaiAdCurrentTime = currentTime;
      const duration = adProgressData.duration;
      let timeRemaining = Math.floor(duration - currentTime);
      if (this.#rmp.adCountDown && timeRemaining >= 0) {
        timeRemaining = FW.readableTime(timeRemaining);
        FW.setText(this.#rmp.dom.adCountdown, timeRemaining);
      }
      if (typeof totalAds === 'number' && totalAds > 1) {
        if (!this.#rmp.dom.adPod) {
          this.#rmp.dom.adPod = FW.createElement('span', 'rmp-ad-info-pod');
          if (this.#rmp.dom.adCountdown) {
            FW.insertBefore(this.#rmp.dom.adInfo, this.#rmp.dom.adPod, this.#rmp.dom.adCountdown);
          } else {
            this.#rmp.dom.adInfo.appendChild(this.#rmp.dom.adPod);
          }
        }
        if (typeof adPosition === 'number') {
          FW.setText(this.#rmp.dom.adPod, adPosition + '/' + totalAds);
        }
      }
    }
  }

  #imaDaiOnAdStarted(e) {
    if (e) {
      if (this.#rmp.adCountDown && this.#rmp.dom.adInfo) {
        if (!this.#rmp.dom.adCountdown) {
          this.#rmp.dom.adCountdown = FW.createElement('span', 'rmp-ad-info-countdown');
          this.#rmp.dom.adInfo.appendChild(this.#rmp.dom.adCountdown);
        }
      }
      if (typeof e.getAd === 'function') {
        const ad = e.getAd();
        this.#rmp.imaAdID = ad.getAdId();
        this.#rmp.imaAdPodInfo = ad.getAdPodInfo();
        this.#rmp.imaAdSystem = ad.getAdSystem();
        this.#rmp.imaAdvertiserName = ad.getAdvertiserName();
        this.#rmp.imaAdApiFramework = ad.getApiFramework();
        this.#rmp.imaAdCreativeAdId = ad.getCreativeAdId();
        this.#rmp.imaAdCreativeId = ad.getCreativeId();
        this.#rmp.imaAdDealId = ad.getDealId();
        this.#rmp.imaAdDescription = ad.getDescription();
        this.#rmp.imaAdDuration = ad.getDuration();
        this.#rmp.imaAdSkipTimeOffset = ad.getSkipTimeOffset();//+
        this.#rmp.imaAdTitle = ad.getTitle();
        this.#rmp.imaAdUniversalAdIds = ad.getUniversalAdIds();//+
        this.#rmp.imaAdLinearHeight = ad.getVastMediaHeight();//+
        this.#rmp.imaAdLinearWidth = ad.getVastMediaWidth();//+
        this.#rmp.imaAdWrapperAdIds = ad.getWrapperAdIds();
        this.#rmp.imaAdWrapperAdSystems = ad.getWrapperAdSystems();
        this.#rmp.imaAdWrapperCreativeIds = ad.getWrapperCreativeIds();
        this.#imaDaiIsSkippable = ad.isSkippable();
        this.#rmp.currentAdForCompanion = ad;
      }
    }
  }

  #finishHlsInit(url) {
    this.#rmp.startingSrcUri = url;
    if (this.#rmp.rmpHlsJS) {
      this.#rmp.rmpHlsJS.initFinised();
    } else if (this.#rmp.rmpInitManagement.imaDaiUseNativeHls) {
      this.#rmp.dom.video.src = this.#rmp.startingSrcUri;
    }
  }

  #onErrorTryToRecover(event) {
    this.#rmp.dispatch('aderror');
    if (this.#rmp.adImaDaiBackupStream) {
      this.#finishHlsInit(this.#rmp.adImaDaiBackupStream);
    } else {
      this.#rmp.rmpUtils.error(202, event);
    }
  }

  #imaDaiOnStreamEvent(event) {
    if (event && event.type) {
      Logger.print('google-ima-dai', event.type);
      switch (event.type) {
        case google.ima.dai.api.StreamEvent.Type.STREAM_INITIALIZED:
          if (this.#rmp.rmpInitManagement.imaDaiPod) {
            const streamId = event.getStreamData().streamId;
            Logger.print('google-ima-dai', 'POD Stream with streamId: ' + streamId);
            if (this.#rmp.adImaDaiVodPod) {
              let url = '';
              if (typeof this.#rmp.adImaDaiVodPodRequestStreamURLCallback === 'function') {
                this.#rmp.adImaDaiVodPodRequestStreamURLCallback({
                  streamId
                }).then(vtpStreamUrl => {
                  Logger.print('google-ima-dai',
                    'VOD POD Stream calling imaDaiStreamManager.loadStreamMetadata with VTP stream URL: ' + vtpStreamUrl);
                  this.#imaDaiStreamManager.loadStreamMetadata();
                  this.#finishHlsInit(vtpStreamUrl);
                }).catch(error => {
                  this.#onErrorTryToRecover(error);
                });
                break;
              } else if (this.#rmp.adImaDaiBackupStream) {
                Logger.print('google-ima-dai', 'PodStream Initialization Failed -> playing backup stream');
                url = this.#rmp.adImaDaiBackupStream;
              } else {
                this.#rmp.rmpUtils.error(202, event);
                break;
              }
              Logger.print('google-ima-dai', 'DAI stream available at ' + url);
              this.#finishHlsInit(url);
            } else {
              let url = '';
              if (streamId) {
                url = this.#rmp.startingSrcUri.replace('[[STREAMID]]', streamId);
                Logger.print('google-ima-dai', 'Live POD Stream calling VTP stream URL: ' + url);
              } else if (this.#rmp.adImaDaiBackupStream) {
                Logger.print('google-ima-dai', 'PodStream Initialization Failed -> playing backup stream');
                url = this.#rmp.adImaDaiBackupStream;
              } else {
                this.#rmp.rmpUtils.error(202, event);
                break;
              }
              Logger.print('google-ima-dai', 'DAI stream available at ' + url);
              this.#finishHlsInit(url);
            }
          }
          this.#rmp.dispatch('adstreaminitialized');
          break;
        case google.ima.dai.api.StreamEvent.Type.LOADED:
          if (!this.#rmp.rmpInitManagement.imaDaiPod) {
            Logger.print('google-ima-dai', 'DAI stream available at ' + event.getStreamData().url);
            if (event.getStreamData().url) {
              this.#finishHlsInit(event.getStreamData().url);
            } else {
              this.#onErrorTryToRecover();
            }
          }
          this.#rmp.dispatch('adloaded');
          break;
        case google.ima.dai.api.StreamEvent.Type.AD_BREAK_STARTED:
          this.#rmp.imaAdOnStage = true;
          this.#rmp.imaAdLinear = true;
          this.#rmp.rmpAdsHelpers.setUIForAds(true);
          FW.show(this.#imaDaiClickElement);
          this.#rmp.dispatch('adbreakstarted');
          break;
        case google.ima.dai.api.StreamEvent.Type.AD_BREAK_ENDED:
          this.#rmp.imaAdLinear = false;
          this.#rmp.imaAdOnStage = false;
          this.#rmp.rmpAdsHelpers.setUIForAds(false);
          FW.hide(this.#imaDaiClickElement);
          this.#rmp.dispatch('adbreakended');
          break;
        case google.ima.dai.api.StreamEvent.Type.AD_PERIOD_STARTED:
          this.#rmp.dispatch('adperiodstarted');
          break;
        case google.ima.dai.api.StreamEvent.Type.AD_PERIOD_ENDED:
          this.#rmp.dispatch('adperiodended');
          break;
        case google.ima.dai.api.StreamEvent.Type.AD_PROGRESS:
          this.#imaDaiOnAdProgress(event);
          this.#rmp.dispatch('adprogress');
          break;
        case google.ima.dai.api.StreamEvent.Type.CUEPOINTS_CHANGED:
          this.#rmp.dispatch('adcuepointschanged');
          break;
        case google.ima.dai.api.StreamEvent.Type.CLICK:
          this.#rmp.dispatch('adclick');
          break;
        case google.ima.dai.api.StreamEvent.Type.ERROR:
          Logger.print('google-ima-dai', 'error loading DAI stream -> trying to play backup stream', event);
          this.#imaDaiErrorMessage = event.getStreamData().errorMessage;
          this.#onErrorTryToRecover(event);
          break;
        case google.ima.dai.api.StreamEvent.Type.STARTED:
          this.#imaDaiOnAdStarted(event);
          this.#rmp.dispatch('adstarted');
          break;
        case google.ima.dai.api.StreamEvent.Type.FIRST_QUARTILE:
          this.#rmp.dispatch('adfirstquartile');
          break;
        case google.ima.dai.api.StreamEvent.Type.MIDPOINT:
          this.#rmp.dispatch('admidpoint');
          break;
        case google.ima.dai.api.StreamEvent.Type.THIRD_QUARTILE:
          this.#rmp.dispatch('adthirdquartile');
          break;
        case google.ima.dai.api.StreamEvent.Type.COMPLETE:
          this.#rmp.dispatch('adcomplete');
          break;
        case google.ima.dai.api.StreamEvent.Type.SKIPPABLE_STATE_CHANGED:
          this.#rmp.dispatch('adskippablestatechanged');
          break;
        case google.ima.dai.api.StreamEvent.Type.SKIPPED:
          this.#rmp.dispatch('adskipped');
          break;
        case google.ima.dai.api.StreamEvent.Type.VIDEO_CLICKED:
          this.#rmp.rmpUIManagement.showChrome();
          this.#rmp.dispatch('advideoclicked');
          break;
        default:
          break;
      }
    }
  }

  #initAdParserBlocked() {
    Logger.print('google-ima-dai', 'IMA DAI library blocked -> use backup stream');
    window.clearInterval(this.#imaDaiLibLoadedInterval);
    if (this.#rmp.adImaDaiBackupStream) {
      this.#finishHlsInit(this.#rmp.adImaDaiBackupStream);
    } else {
      this.#rmp.rmpUtils.error(202);
    }
  }

  #postInit() {
    Logger.print('google-ima-dai', 'init');
    window.clearInterval(this.#imaDaiLibLoadedInterval);
    this.#imaDaiClickElement = FW.createElement('div', 'rmp-ima-dai-click-area');
    this.#rmp.dom.content.appendChild(this.#imaDaiClickElement);
    const settings = new google.ima.dai.api.UiSettings();
    if (this.#rmp.adLocale === 'auto') {
      this.#rmp.adLocale = Language.codes[this.#rmp.rmpInitManagement.validatedLanguage][0];
    }
    settings.setLocale(this.#rmp.adLocale);
    this.#imaDaiStreamManager = new google.ima.dai.api.StreamManager(
      this.#rmp.dom.video,
      this.#imaDaiClickElement,
      settings
    );
    this.#imaDaiStreamManager.addEventListener(
      [
        google.ima.dai.api.StreamEvent.Type.LOADED,
        google.ima.dai.api.StreamEvent.Type.AD_BREAK_STARTED,
        google.ima.dai.api.StreamEvent.Type.AD_BREAK_ENDED,
        google.ima.dai.api.StreamEvent.Type.AD_PERIOD_STARTED,
        google.ima.dai.api.StreamEvent.Type.AD_PERIOD_ENDED,
        google.ima.dai.api.StreamEvent.Type.AD_PROGRESS,
        google.ima.dai.api.StreamEvent.Type.CUEPOINTS_CHANGED,
        google.ima.dai.api.StreamEvent.Type.CLICK,
        google.ima.dai.api.StreamEvent.Type.ERROR,
        google.ima.dai.api.StreamEvent.Type.STARTED,
        google.ima.dai.api.StreamEvent.Type.FIRST_QUARTILE,
        google.ima.dai.api.StreamEvent.Type.MIDPOINT,
        google.ima.dai.api.StreamEvent.Type.STREAM_INITIALIZED,
        google.ima.dai.api.StreamEvent.Type.THIRD_QUARTILE,
        google.ima.dai.api.StreamEvent.Type.COMPLETE,
        google.ima.dai.api.StreamEvent.Type.SKIPPABLE_STATE_CHANGED,
        google.ima.dai.api.StreamEvent.Type.SKIPPED,
        google.ima.dai.api.StreamEvent.Type.VIDEO_CLICKED
      ],
      this.#imaDaiOnStreamEvent.bind(this),
      false);
    if (this.#rmp.rmpInitManagement.imaDaiUseNativeHls) {
      Logger.print('google-ima-dai', 'set up using onTimedMetadata');
      this.#rmp.dom.video.textTracks.addEventListener('addtrack', e => {
        const track = e.track;
        if (track.kind === 'metadata') {
          track.mode = 'hidden';
          track.addEventListener('cuechange', () => {
            for (const cue of track.activeCues) {
              const metadata = {};
              metadata[cue.value.key] = cue.value.data;
              this.#imaDaiStreamManager.onTimedMetadata(metadata);
            }
          });
        }
      });
    } else {
      this.#rmp.hlsJS.on(window.Hls.Events.FRAG_PARSING_METADATA, (event, data) => {
        if (this.#imaDaiStreamManager && data) {
          // For each ID3 tag in our metadata, we pass in the type - ID3, the
          // tag data (a byte array), and the presentation timestamp (PTS).
          data.samples.forEach(sample => {
            this.#imaDaiStreamManager.processMetadata('ID3', sample.data, sample.pts);
          });
        }
      });
    }
    let omidAccessModeRules = null;
    if (!FW.isEmptyObject(this.#rmp.omidAccessModeRules)) {
      omidAccessModeRules = AdsHelpers.getOmidAccessModeRules(this.#rmp.omidAccessModeRules);
    }
    if (this.#rmp.rmpInitManagement.imaDaiLive) {
      this.#imaDaiRequestLiveStream(
        this.#rmp.adImaDaiLiveAssetKey,
        this.#rmp.adImaDaiApiKey,
        this.#rmp.adImaDaiAdTagParameters,
        this.#rmp.adImaDaiStreamActivityMonitorId,
        this.#rmp.adImaDaiAuthToken,
        omidAccessModeRules
      );
    } else if (this.#rmp.rmpInitManagement.imaDaiVod) {
      // handle snapback
      let isSnapback;
      let snapForwardTime;
      this.#rmp.dom.video.addEventListener('seeked', () => {
        if (isSnapback) {
          isSnapback = false;
          return;
        }
        const currentTime = this.#rmp.dom.video.currentTime;
        const previousCuePoint = this.#imaDaiStreamManager.previousCuePointForStreamTime(currentTime);
        if (previousCuePoint && !previousCuePoint.played) {
          Logger.print('google-ima-dai',
            'Seeking back to ' + previousCuePoint.start + ' and will return to ' + currentTime);
          isSnapback = true;
          snapForwardTime = currentTime;
          this.#rmp.dom.video.currentTime = previousCuePoint.start;
        }
      });
      this.#imaDaiStreamManager.addEventListener(google.ima.dai.api.StreamEvent.Type.AD_BREAK_ENDED, () => {
        if (snapForwardTime && snapForwardTime > this.#rmp.dom.video.currentTime) {
          this.#rmp.dom.video.currentTime = snapForwardTime;
          snapForwardTime = null;
        }
      });
      this.#imaDaiRequestVODStream(
        this.#rmp.adImaDaiVodContentSourceId,
        this.#rmp.adImaDaiVodVideoId,
        this.#rmp.adImaDaiApiKey,
        this.#rmp.adImaDaiAdTagParameters,
        this.#rmp.adImaDaiStreamActivityMonitorId,
        this.#rmp.adImaDaiAuthToken,
        omidAccessModeRules
      );
    } else if (this.#rmp.rmpInitManagement.imaDaiPod) {
      if (this.#rmp.adImaDaiVodPod) {
        this.#imaDaiRequestPodVodStreamRequest(
          this.#rmp.adImaDaiPodNetworkCode,
          this.#rmp.adImaDaiApiKey,
          this.#rmp.adImaDaiAdTagParameters,
          this.#rmp.adImaDaiStreamActivityMonitorId,
          this.#rmp.adImaDaiAuthToken,
          omidAccessModeRules
        );
      } else {
        this.#imaDaiRequestPodStream(
          this.#rmp.adImaDaiCustomAssetKey,
          this.#rmp.adImaDaiPodNetworkCode,
          this.#rmp.adImaDaiApiKey,
          this.#rmp.adImaDaiAdTagParameters,
          this.#rmp.adImaDaiStreamActivityMonitorId,
          this.#rmp.adImaDaiAuthToken,
          omidAccessModeRules
        );
      }
    }
  }

  #preInit() {
    // case ad blocker
    if (this.#rmp.adParserLoadError) {
      this.#initAdParserBlocked();
      return;
    }
    this.#postInit();
  }

  destroy() {
    window.clearInterval(this.#imaDaiLibLoadedInterval);
  }

  init() {
    // if preload is not none or autoplay
    // we need to wait for DAI lib to load
    if (!AdsHelpers.isGoogleImaDaiAvailable()) {
      this.#imaDaiLibLoadedInterval = window.setInterval(() => {
        if (AdsHelpers.isGoogleImaDaiAvailable()) {
          this.#preInit();
        }
      }, 40);
      return;
    }
    this.#preInit();
  }

}
